/**
 * Data Store
 *
 * Unified store for managing data page state including projects, memory, and health status
 */
import { makeAutoObservable, runInAction } from 'mobx'
import { apiClient } from '@/services/api'
import { knowledgeBaseStore } from './KnowledgeBaseStore'
import { knowledgeGraphStore } from './KnowledgeGraphStore'

// Types
interface LayerStats {
  name: string
  key: string
  node_count: number
  avg_confidence: number | null
  description: string
}

interface Episode {
  id: string
  episode_type: string
  execution_id?: string
  agent_id?: string
  quality_score: number
  confidence: number
  timestamp?: string
  consolidated: boolean
  content?: string
  summary?: string
  domain?: string
}

interface Entity {
  id: string
  name: string
  entity_type: string
  domain: string
  subdomain?: string
  confidence: number
  corroboration_count: number
  access_count: number
  tags: string[]
  created_at?: string
  updated_at?: string
  description?: string
}

interface HealthStatus {
  status: string
  memgraph: boolean
  weaviate: boolean
}

interface Project {
  id: string
  name: string
  description?: string
}

class DataStore {
  // Project selection
  selectedProjectId: string | null = null
  projects: Project[] = []

  // Health status
  health: HealthStatus | null = null
  layers: LayerStats[] = []

  // Memory data
  episodes: Episode[] = []
  entities: Entity[] = []

  // Loading states
  isLoading = false
  isLoadingProjects = false
  isLoadingHealth = false
  isLoadingMemory = false
  error: string | null = null

  // Memory tab state
  memoryActiveTab: 'episodes' | 'entities' = 'episodes'
  episodeTypeFilter = ''
  entityTypeFilter = ''
  memorySearchQuery = ''

  constructor() {
    makeAutoObservable(this)
  }

  // Computed properties
  get hasProjects() {
    return this.projects.length > 0
  }

  get selectedProject() {
    return this.projects.find((p) => p.id === this.selectedProjectId) || null
  }

  get isHealthy() {
    return this.health?.status === 'healthy'
  }

  get isDegraded() {
    return this.health?.status === 'degraded'
  }

  get totalGraphs() {
    return knowledgeGraphStore.totalGraphs
  }

  get totalCollections() {
    return knowledgeBaseStore.collections.length
  }

  get totalEpisodes() {
    return this.episodes.length
  }

  get totalEntities() {
    return this.entities.length
  }

  get layerStats() {
    return this.layers.reduce(
      (acc, layer) => {
        acc[layer.key] = layer.node_count
        return acc
      },
      {} as Record<string, number>
    )
  }

  // Actions
  setSelectedProjectId(projectId: string | null) {
    this.selectedProjectId = projectId
  }

  setMemoryActiveTab(tab: 'episodes' | 'entities') {
    this.memoryActiveTab = tab
  }

  setEpisodeTypeFilter(filter: string) {
    this.episodeTypeFilter = filter
  }

  setEntityTypeFilter(filter: string) {
    this.entityTypeFilter = filter
  }

  setMemorySearchQuery(query: string) {
    this.memorySearchQuery = query
  }

  clearError() {
    this.error = null
  }

  // Fetch projects
  async fetchProjects() {
    this.isLoadingProjects = true
    this.error = null

    try {
      const response = await apiClient.getWorkspaces()

      runInAction(() => {
        // Map workspaces to projects
        this.projects = response.items.map((w: any) => ({
          id: w.id,
          name: w.name,
          description: w.description,
        }))
        this.isLoadingProjects = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to fetch projects'
        this.isLoadingProjects = false
      })
    }
  }

  // Fetch health status
  async fetchHealth() {
    this.isLoadingHealth = true

    try {
      const response = await apiClient.get<HealthStatus>('/api/v1/memory/health')

      runInAction(() => {
        this.health = response.data
        this.isLoadingHealth = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.health = { status: 'unhealthy', memgraph: false, weaviate: false }
        this.isLoadingHealth = false
      })
    }
  }

  // Fetch layer statistics
  async fetchLayers() {
    try {
      const response = await apiClient.get<{ layers: LayerStats[] }>('/api/v1/memory/layers')

      runInAction(() => {
        this.layers = response.data.layers
      })
    } catch (error: any) {
      console.error('Failed to fetch layers:', error)
    }
  }

  // Fetch episodes
  async fetchEpisodes() {
    this.isLoadingMemory = true

    try {
      const params: Record<string, any> = { limit: 100 }
      if (this.episodeTypeFilter) params.episode_type = this.episodeTypeFilter

      const response = await apiClient.get<Episode[]>('/api/v1/memory/episodes', { params })

      runInAction(() => {
        this.episodes = response.data
        this.isLoadingMemory = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.episodes = []
        this.isLoadingMemory = false
      })
    }
  }

  // Fetch entities
  async fetchEntities() {
    this.isLoadingMemory = true

    try {
      const params: Record<string, any> = { limit: 100 }
      if (this.entityTypeFilter) params.entity_type = this.entityTypeFilter

      const response = await apiClient.get<Entity[]>('/api/v1/memory/entities', { params })

      runInAction(() => {
        this.entities = response.data
        this.isLoadingMemory = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.entities = []
        this.isLoadingMemory = false
      })
    }
  }

  // Search episodes
  async searchEpisodes(query: string) {
    if (!query.trim()) {
      return this.fetchEpisodes()
    }

    this.isLoadingMemory = true

    try {
      const response = await apiClient.get<{ results: any[] }>('/api/v1/memory/search/episodes', {
        params: { query, limit: 50 },
      })

      runInAction(() => {
        this.episodes = response.data.results.map((r: any) => ({
          id: r.episodeId,
          episode_type: r.episodeType,
          content: r.content,
          summary: r.summary,
          domain: r.domain,
          quality_score: r.qualityScore || 5,
          confidence: r._additional?.certainty || 0.5,
          timestamp: r.timestamp,
          consolidated: false,
        }))
        this.isLoadingMemory = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.isLoadingMemory = false
      })
      throw error
    }
  }

  // Search entities
  async searchEntities(query: string) {
    if (!query.trim()) {
      return this.fetchEntities()
    }

    this.isLoadingMemory = true

    try {
      const response = await apiClient.get<{ results: any[] }>('/api/v1/memory/search/entities', {
        params: { query, limit: 50 },
      })

      runInAction(() => {
        this.entities = response.data.results.map((r: any) => ({
          id: r.entityId,
          name: r.name,
          entity_type: r.entityType,
          domain: r.domain,
          description: r.description,
          tags: r.tags || [],
          confidence: r.confidence || 0.5,
          corroboration_count: 1,
          access_count: 0,
        }))
        this.isLoadingMemory = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.isLoadingMemory = false
      })
      throw error
    }
  }

  // Create episode
  async createEpisode(data: {
    episode_type: string
    content: string
    quality_score: number
    domain: string
  }) {
    await apiClient.post('/api/v1/memory/episodes', data)
    await Promise.all([this.fetchEpisodes(), this.fetchLayers()])
  }

  // Create entity
  async createEntity(data: {
    name: string
    entity_type: string
    description: string
    domain: string
    tags: string[]
    confidence: number
  }) {
    await apiClient.post('/api/v1/memory/entities', data)
    await Promise.all([this.fetchEntities(), this.fetchLayers()])
  }

  // Update entity
  async updateEntity(
    id: string,
    data: {
      name?: string
      description?: string
      tags?: string[]
      confidence?: number
    }
  ) {
    await apiClient.patch(`/api/v1/memory/entities/${id}`, data)
    await this.fetchEntities()
  }

  // Delete episode
  async deleteEpisode(id: string) {
    await apiClient.delete(`/api/v1/memory/episodes/${id}`)
    await Promise.all([this.fetchEpisodes(), this.fetchLayers()])
  }

  // Delete entity
  async deleteEntity(id: string) {
    await apiClient.delete(`/api/v1/memory/entities/${id}`)
    await Promise.all([this.fetchEntities(), this.fetchLayers()])
  }

  // Fetch all data
  async fetchAllData(projectId?: string) {
    this.isLoading = true
    this.error = null

    if (projectId !== undefined) {
      this.selectedProjectId = projectId
    }

    try {
      await Promise.all([
        this.fetchProjects(),
        this.fetchHealth(),
        this.fetchLayers(),
        this.fetchEpisodes(),
        this.fetchEntities(),
        knowledgeBaseStore.fetchCollections(),
        knowledgeGraphStore.fetchGraphs(this.selectedProjectId || undefined),
      ])

      runInAction(() => {
        this.isLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to fetch data'
        this.isLoading = false
      })
    }
  }

  // Refresh data
  async refresh() {
    await this.fetchAllData(this.selectedProjectId || undefined)
  }

  // Reset store
  reset() {
    this.selectedProjectId = null
    this.projects = []
    this.health = null
    this.layers = []
    this.episodes = []
    this.entities = []
    this.isLoading = false
    this.isLoadingProjects = false
    this.isLoadingHealth = false
    this.isLoadingMemory = false
    this.error = null
    this.memoryActiveTab = 'episodes'
    this.episodeTypeFilter = ''
    this.entityTypeFilter = ''
    this.memorySearchQuery = ''
  }
}

export const dataStore = new DataStore()
export default dataStore
